import {
  SalesTaxExemptCustomerVisibleColumnEnum,
  SalesTaxExemptionJurisdictionVisibleColumnEnum,
  SalesTaxProductVisibleColumnEnum,
  SalesTaxTransactionVisibleColumnEnum,
} from '../sales-tax/types';

export enum SettingEntityType {
  COMPANY = 'COMPANY',
  CORPORATION = 'CORPORATION',
  AGENT_FIRM = 'AGENT_FIRM',
  USER = 'USER',
}

export enum SettingKey {
  SALES_TAX_DISMISS_REGISTRATION_TASKS = 'SALES_TAX_DISMISS_REGISTRATION_TASKS',
  SALES_TAX_GENERATE_NEXUS_REPORT = 'SALES_TAX_GENERATE_NEXUS_REPORT',
  SALES_TAX_TRANSACTION_VISIBLE_COLUMNS = 'SALES_TAX_TRANSACTION_VISIBLE_COLUMNS',
  SALES_TAX_PRODUCT_VISIBLE_COLUMNS = 'SALES_TAX_PRODUCT_VISIBLE_COLUMNS',
  SALES_TAX_EXEMPT_CUSTOMER_VISIBLE_COLUMNS = 'SALES_TAX_EXEMPT_CUSTOMER_VISIBLE_COLUMNS',
  SALES_TAX_EXEMPTION_JURISDICTIONS_VISIBLE_COLUMNS = 'SALES_TAX_EXEMPTION_JURISDICTIONS_VISIBLE_COLUMNS',
  SALES_TAX_TEST_MODE = 'SALES_TAX_TEST_MODE',
  DEMO_MODE_AGENT_EMAIL = 'DEMO_MODE_AGENT_EMAIL',
  SALES_TAX_EXEMPTION_REPLY_TO_EMAIL = 'SALES_TAX_EXEMPTION_REPLY_TO_EMAIL',
  SALES_TAX_EXEMPTION_ALLOW_CUSTOMER_REPLIES = 'SALES_TAX_EXEMPTION_ALLOW_CUSTOMER_REPLIES',
  SALES_TAX_AUTO_APPROVE_SOURCE_RECORDS = 'SALES_TAX_AUTO_APPROVE_SOURCE_RECORDS',
  INDIRECT_TAX_TRANSACTIONS_UPLOAD_METHOD = 'INDIRECT_TAX_TRANSACTIONS_UPLOAD_METHOD',
  BOOK_CALL_WITH_ACCOUNT_MANAGER = 'BOOK_CALL_WITH_ACCOUNT_MANAGER',
}

export enum TransactionDataUploadMethod {
  MANUAL_UPLOAD = 'MANUAL_UPLOAD',
  MANUAL_AND_INTEGRATION = 'MANUAL_AND_INTEGRATION',
  INTEGRATION_ONLY = 'INTEGRATION_ONLY',
}

export interface DismissedRegistrationTask {
  jurisdiction: string;
  notes?: string | null;
}

export type SettingValueType = {
  [SettingKey.SALES_TAX_DISMISS_REGISTRATION_TASKS]: DismissedRegistrationTask[];
  [SettingKey.SALES_TAX_GENERATE_NEXUS_REPORT]: boolean;
  [SettingKey.SALES_TAX_TRANSACTION_VISIBLE_COLUMNS]: SalesTaxTransactionVisibleColumnEnum[];
  [SettingKey.SALES_TAX_PRODUCT_VISIBLE_COLUMNS]: SalesTaxProductVisibleColumnEnum[];
  [SettingKey.SALES_TAX_EXEMPT_CUSTOMER_VISIBLE_COLUMNS]: SalesTaxExemptCustomerVisibleColumnEnum[];
  [SettingKey.SALES_TAX_EXEMPTION_JURISDICTIONS_VISIBLE_COLUMNS]: SalesTaxExemptionJurisdictionVisibleColumnEnum[];
  [SettingKey.SALES_TAX_TEST_MODE]: boolean;
  [SettingKey.DEMO_MODE_AGENT_EMAIL]: string;
  [SettingKey.SALES_TAX_EXEMPTION_REPLY_TO_EMAIL]: string;
  [SettingKey.SALES_TAX_EXEMPTION_ALLOW_CUSTOMER_REPLIES]: boolean;
  [SettingKey.SALES_TAX_AUTO_APPROVE_SOURCE_RECORDS]: boolean;
  [SettingKey.INDIRECT_TAX_TRANSACTIONS_UPLOAD_METHOD]: TransactionDataUploadMethod;
  [SettingKey.BOOK_CALL_WITH_ACCOUNT_MANAGER]: boolean;
};

export interface SettingEntity {
  entityType: SettingEntityType;
  entityId: number;
}

export interface Setting {
  id: string;
  key: SettingKey;
  value: SettingValueType[SettingKey];
  entities: SettingEntity[];
  companyId?: number;
  agentFirmId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SettingResponse {
  success: boolean;
  data?: Setting;
  error?: string;
}

export interface SettingsListResponse {
  success: boolean;
  data?: Setting[];
  error?: string;
}

export interface SetSettingPayload {
  key: SettingKey;
  value: SettingValueType[SettingKey];
  entities: SettingEntity[];
}

/**
 * Normalizes dismissed registration tasks from old format (jurisdiction strings)
 * to new format (DismissedRegistrationTask[]) for backward compatibility.
 */
export function normalizeDismissedRegistrationTasks(
  data: unknown
): DismissedRegistrationTask[] {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  // Check if already in new format
  if (data.length > 0 && typeof data[0] === 'object' && 'jurisdiction' in data[0]) {
    return data as DismissedRegistrationTask[];
  }
  // Old format: array of Jurisdiction strings
  return (data as string[]).map((jurisdiction) => ({
    jurisdiction,
    notes: null,
  }));
}
